import { Link, useNavigate } from "react-router-dom";
import { Leaf, LogOut, LayoutDashboard, LifeBuoy, ShieldAlert, Menu } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useState } from "react";

export function DisclaimerBanner({ className = "" }) {
  return (
    <div
      data-testid="medical-disclaimer"
      className={`flex items-start gap-2 text-xs text-[#5A6B61] bg-[#EBE5DC] border border-[#2C4A3E]/15 rounded-lg px-3 py-2 ${className}`}
    >
      <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-[#C47A53]" />
      <span>
        Educational content only. This material is not medical advice and does not replace
        care from a licensed healthcare professional. Government licensing or certification is not
        required, as there are no federal or state licensing or certification boards for herbalists
        in the United States.
      </span>
    </div>
  );
}

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[#FAF8F5]/80 border-b border-[#2C4A3E]/10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-full bg-[#2C4A3E] flex items-center justify-center">
            <Leaf className="w-5 h-5 text-[#E8ECE8]" />
          </span>
          <span className="font-serif text-xl font-semibold text-[#1A2621] leading-none">
            Mystic Brew <span className="text-[#829B88]">Academy</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              <Link to="/dashboard" data-testid="nav-dashboard" className="px-3 py-2 text-sm font-medium text-[#2C4A3E] hover:text-[#223B31] flex items-center gap-1.5">
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="px-3 py-2 text-sm font-medium text-[#C47A53] hover:underline">
                  Owner Panel
                </Link>
              )}
              <Link to="/support" data-testid="nav-support" className="px-3 py-2 text-sm font-medium text-[#2C4A3E] flex items-center gap-1.5">
                <LifeBuoy className="w-4 h-4" /> Support
              </Link>
              <button onClick={onLogout} data-testid="logout-btn" className="ml-1 px-4 py-2 text-sm font-medium rounded-full border border-[#2C4A3E]/25 text-[#2C4A3E] hover:bg-[#2C4A3E] hover:text-[#FAF8F5] transition-colors flex items-center gap-1.5">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/support" data-testid="nav-support" className="px-3 py-2 text-sm font-medium text-[#2C4A3E]">Support</Link>
              <Link to="/login" data-testid="nav-login" className="px-4 py-2 text-sm font-medium text-[#2C4A3E]">Log in</Link>
              <Link to="/register" data-testid="nav-register" className="px-5 py-2 text-sm font-semibold rounded-full bg-[#2C4A3E] text-[#FAF8F5] hover:bg-[#223B31] transition-colors">
                Enroll Now
              </Link>
            </>
          )}
        </nav>

        <button className="md:hidden p-2 text-[#2C4A3E]" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
          <Menu className="w-6 h-6" />
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-[#2C4A3E]/10 bg-[#FAF8F5] px-4 py-3 flex flex-col gap-2">
          {user ? (
            <>
              <Link to="/dashboard" onClick={() => setOpen(false)} className="py-2 text-[#2C4A3E]">Dashboard</Link>
              {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="py-2 text-[#C47A53]">Owner Panel</Link>}
              <Link to="/support" onClick={() => setOpen(false)} className="py-2 text-[#2C4A3E]">Support</Link>
              <button onClick={onLogout} className="py-2 text-left text-[#2C4A3E]">Sign out</button>
            </>
          ) : (
            <>
              <Link to="/support" onClick={() => setOpen(false)} className="py-2 text-[#2C4A3E]">Support</Link>
              <Link to="/login" onClick={() => setOpen(false)} className="py-2 text-[#2C4A3E]">Log in</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="py-2 text-[#2C4A3E]">Enroll Now</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="mt-20 border-t border-[#2C4A3E]/10 bg-[#EBE5DC]/50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-[#2C4A3E] flex items-center justify-center">
              <Leaf className="w-4 h-4 text-[#E8ECE8]" />
            </span>
            <span className="font-serif text-lg text-[#1A2621]">Mystic Brew Academy</span>
          </div>
          <a href="mailto:WateredSouls@MysticBr8w.com" className="text-sm text-[#2C4A3E] font-mono">
            WateredSouls@MysticBr8w.com
          </a>
        </div>
        <div className="mt-6">
          <DisclaimerBanner />
        </div>
        <p className="mt-4 text-xs text-[#5A6B61]">© {new Date().getFullYear()} Mystic Brew Academy. Herbal education for aspiring practitioners.</p>
      </div>
    </footer>
  );
}

export function Layout({ children }) {
  return (
    <div className="grain min-h-screen flex flex-col relative">
      <Header />
      <main className="flex-1 relative z-10">{children}</main>
      <Footer />
    </div>
  );
}
